import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { inr } from "@/lib/api";
import { clearAdminValidationErrorByTestId } from "./adminValidation";

const createCustomItem = () => ({
  id: window.crypto?.randomUUID?.() || `custom-${Date.now()}-${Math.random()}`,
  name: "",
  additional_price: 0,
  stock: 0,
  active: true,
});

export const HamperConfig = ({ value, onChange, products, editingId }) => {
  const [search, setSearch] = useState("");
  const options = value.hamper_options || [];
  const customItems = value.hamper_custom_items || [];
  const candidates = products
    .filter((product) => (
      product.id !== editingId
      && product.product_type !== "gift_box"
      && product.active !== false
    ))
    .flatMap((product) => (
      product.variants?.length ? product.variants : [product]
    ).map((variant) => ({
      product_id: product.id,
      name: product.name,
      variant_label: variant.label || null,
      price: variant.price,
      stock: variant.stock ?? product.stock,
    })))
    .filter((item) => `${item.name} ${item.variant_label || ""}`.toLowerCase().includes(
      search.toLowerCase(),
    ));
  const updateOptions = (next) => {
    onChange({ ...value, hamper_options: next });
    if (next.length) {
      clearAdminValidationErrorByTestId("admin-hamper-config");
    }
  };
  const updateCustomItems = (next) => {
    onChange({ ...value, hamper_custom_items: next });
    if (next.length) {
      clearAdminValidationErrorByTestId("admin-hamper-config");
    }
  };

  return (
    <section
      className="mb-4 rounded-xl border border-[#D9C3A5] bg-[#FDFBF7] p-4"
      data-validation-surface="self"
      data-testid="admin-hamper-config"
    >
      <h4 className="font-semibold">Gift Box / Hamper contents</h4>
      <p className="mt-1 text-sm text-[#64748B]">
        Customers choose the exact item count. The final price is the base box price plus
        selected-item additions.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium">
          Base box price (₹)
          <input
            type="number"
            min="0"
            step="0.01"
            value={value.price ?? 0}
            onChange={(event) => onChange({ ...value, price: Number(event.target.value || 0) })}
            className="mt-1 block w-full rounded-lg border border-[#E2DCD2] px-3 py-2"
            data-testid="admin-hamper-base-price"
          />
        </label>
        <label className="block text-sm font-medium">
          Box-level stock
          <input
            type="number"
            min="0"
            step="1"
            value={value.box_stock ?? 0}
            onChange={(event) => onChange({
              ...value,
              box_stock: Number(event.target.value || 0),
            })}
            className="mt-1 block w-full rounded-lg border border-[#E2DCD2] px-3 py-2"
            data-testid="admin-hamper-box-stock"
          />
          <span className="mt-1 block text-xs font-normal text-[#64748B]">
            Limits completed boxes in addition to each selected item’s stock.
          </span>
        </label>
        <label className="block text-sm font-medium">
          Required items per box
          <input
            type="number"
            min="1"
            max="100"
            step="1"
            value={value.hamper_required_count ?? 2}
            onChange={(event) => onChange({
              ...value,
              hamper_required_count: event.target.value === "" ? "" : Number(event.target.value),
            })}
            className="mt-1 block w-full rounded-lg border border-[#E2DCD2] px-3 py-2"
            data-testid="admin-hamper-required-count"
          />
        </label>
      </div>
      <label className="mt-4 block text-sm font-medium">
        Find catalogue items
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search products or variants"
          className="mt-1 w-full rounded-lg border border-[#E2DCD2] px-3 py-2"
          data-testid="admin-hamper-search"
        />
      </label>
      <div
        className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-[#E2DCD2] bg-white"
        data-testid="admin-hamper-candidates"
      >
        {candidates.map((item, index) => {
          const added = options.some((option) => (
            option.product_id === item.product_id && option.variant_label === item.variant_label
          ));
          return (
            <button
              type="button"
              key={`${item.product_id}-${item.variant_label}`}
              disabled={added}
              onClick={() => updateOptions([
                ...options,
                {
                  product_id: item.product_id,
                  variant_label: item.variant_label,
                  additional_price: 0,
                },
              ])}
              className={[
                "flex w-full items-center justify-between gap-3 border-b border-[#E2DCD2]",
                "px-3 py-2 text-left text-sm transition-colors last:border-b-0",
                "hover:bg-[#EBF2EE] disabled:opacity-40",
              ].join(" ")}
              data-testid={`admin-hamper-add-item-${index}`}
            >
              <span>
                {item.name} · {item.variant_label || "Product"}
                <span className="ml-2 text-xs text-[#64748B]">
                  {inr(item.price)} · Stock {item.stock}
                </span>
              </span>
              {added ? "Added" : <Plus size={16} />}
            </button>
          );
        })}
        {!candidates.length && (
          <p className="p-3 text-sm text-[#64748B]" data-testid="admin-hamper-search-empty">
            No matching catalogue items.
          </p>
        )}
      </div>
      <div className="mt-4 space-y-3" data-testid="admin-hamper-item-list">
        {options.map((option, index) => {
          const product = products.find((item) => item.id === option.product_id);
          return (
            <div
              key={`${option.product_id}-${option.variant_label}`}
              className={[
                "grid items-center gap-3 rounded-lg border border-[#E2DCD2] bg-white",
                "p-3 sm:grid-cols-[minmax(0,1fr)_180px_40px]",
              ].join(" ")}
              data-testid={`admin-hamper-option-${index}`}
            >
              <p className="min-w-0 text-sm font-medium">
                {product?.name || "Unavailable product"}
                <span className="block text-xs text-[#64748B]">{option.variant_label}</span>
              </p>
              <label className="text-xs text-[#64748B]">
                Additional price (₹)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={option.additional_price ?? option.price ?? 0}
                  onChange={(event) => updateOptions(options.map((item, itemIndex) => (
                    itemIndex === index
                      ? { ...item, additional_price: Number(event.target.value || 0) }
                      : item
                  )))}
                  className={[
                    "mt-1 w-full rounded-lg border border-[#E2DCD2] px-3 py-2 text-sm",
                    "text-[#1E2A24]",
                  ].join(" ")}
                  data-testid={`admin-hamper-additional-price-${index}`}
                />
              </label>
              <button
                type="button"
                onClick={() => updateOptions(options.filter((_, itemIndex) => itemIndex !== index))}
                className="justify-self-end rounded p-2 text-red-600 hover:bg-red-50 sm:mt-5"
                aria-label="Remove selectable item"
                data-testid={`admin-hamper-remove-${index}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>
      <div
        className="mt-6 border-t border-[#E2DCD2] pt-5"
        data-testid="admin-hamper-custom-items"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h5 className="font-semibold text-[#1E2A24]">Box-only custom items</h5>
            <p className="mt-1 text-xs text-[#64748B]">
              These items exist only inside this Gift Box and never enter the catalogue or search.
            </p>
          </div>
          <button
            type="button"
            onClick={() => updateCustomItems([...customItems, createCustomItem()])}
            className={[
              "inline-flex items-center gap-1 rounded border border-[#2D5A47] px-3 py-2",
              "text-xs font-semibold text-[#2D5A47] transition-colors hover:bg-[#EBF2EE]",
            ].join(" ")}
            data-testid="admin-hamper-add-custom-item"
          >
            <Plus size={14} /> Add custom item
          </button>
        </div>
        <div className="mt-4 space-y-3">
          {customItems.map((item, index) => (
            <div
              key={item.id || index}
              className={[
                "grid gap-3 rounded-lg border border-[#E2DCD2] bg-white p-3",
                "lg:grid-cols-[minmax(0,1.4fr)_150px_130px_110px_40px]",
              ].join(" ")}
              data-testid={`admin-hamper-custom-item-${index}`}
            >
              <label className="text-xs text-[#64748B]">
                Item name
                <input
                  value={item.name}
                  onChange={(event) => updateCustomItems(customItems.map((current, itemIndex) => (
                    itemIndex === index ? { ...current, name: event.target.value } : current
                  )))}
                  className={[
                    "mt-1 w-full rounded-lg border border-[#E2DCD2] px-3 py-2 text-sm",
                    "text-[#1E2A24]",
                  ].join(" ")}
                  data-testid={`admin-hamper-custom-name-${index}`}
                />
              </label>
              <label className="text-xs text-[#64748B]">
                Additional price (₹)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.additional_price ?? 0}
                  onChange={(event) => updateCustomItems(customItems.map((current, itemIndex) => (
                    itemIndex === index
                      ? { ...current, additional_price: Number(event.target.value || 0) }
                      : current
                  )))}
                  className={[
                    "mt-1 w-full rounded-lg border border-[#E2DCD2] px-3 py-2 text-sm",
                    "text-[#1E2A24]",
                  ].join(" ")}
                  data-testid={`admin-hamper-custom-additional-price-${index}`}
                />
              </label>
              <label className="text-xs text-[#64748B]">
                Stock quantity
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={item.stock ?? 0}
                  onChange={(event) => updateCustomItems(customItems.map((current, itemIndex) => (
                    itemIndex === index
                      ? { ...current, stock: Number(event.target.value || 0) }
                      : current
                  )))}
                  className={[
                    "mt-1 w-full rounded-lg border border-[#E2DCD2] px-3 py-2 text-sm",
                    "text-[#1E2A24]",
                  ].join(" ")}
                  data-testid={`admin-hamper-custom-stock-${index}`}
                />
              </label>
              <label className="flex items-end gap-2 pb-2 text-xs font-medium text-[#1E2A24]">
                <input
                  type="checkbox"
                  checked={item.active !== false}
                  onChange={(event) => updateCustomItems(customItems.map((current, itemIndex) => (
                    itemIndex === index ? { ...current, active: event.target.checked } : current
                  )))}
                  data-testid={`admin-hamper-custom-active-${index}`}
                />
                Active
              </label>
              <button
                type="button"
                onClick={() => updateCustomItems(
                  customItems.filter((_, itemIndex) => itemIndex !== index),
                )}
                className="self-end justify-self-end rounded p-2 text-red-600 hover:bg-red-50"
                aria-label="Remove custom box item"
                data-testid={`admin-hamper-custom-remove-${index}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
